import React from 'react';
import {
  Text, UnorderedList, ListItem, Alert, AlertIcon, Table, Thead, Tbody, Tr, Th, Td,
  Badge, HStack,
} from '@chakra-ui/react';

const financeiroManual = [
  {
    title: '1. Visão Geral — Finanças',
    content: (
      <>
        <Text mb={3}>
          O módulo de Finanças permite acompanhar toda a movimentação financeira da plataforma TOVI. Acesse via menu lateral: <strong>Finanças</strong>.
        </Text>
        <Text mb={2}>Páginas disponíveis:</Text>
        <UnorderedList ml={6} spacing={1}>
          <ListItem><strong>Fluxo de Caixa</strong> — visão consolidada de receitas e despesas</ListItem>
          <ListItem><strong>Pagamentos</strong> — todos os pagamentos processados via Pagar.me</ListItem>
          <ListItem><strong>Assinaturas</strong> — assinaturas premium dos profissionais</ListItem>
          <ListItem><strong>Contas a Receber</strong> — visão contábil dos recebimentos</ListItem>
          <ListItem><strong>Contas a Pagar</strong> — registro de despesas operacionais</ListItem>
        </UnorderedList>
      </>
    ),
    keywords: 'finanças visão geral fluxo caixa pagamentos assinaturas contas',
  },
  {
    title: '2. Fluxo de Caixa',
    content: (
      <>
        <Text mb={3}>Mostra receitas e despesas em uma visão consolidada com gráficos.</Text>
        <UnorderedList ml={6} spacing={1} mb={3}>
          <ListItem><strong>Cards de resumo:</strong> Receitas Totais, Despesas Totais, Saldo do Período</ListItem>
          <ListItem><strong>Tabela de transações:</strong> lista todas as movimentações com data, descrição, categoria e valor</ListItem>
          <ListItem><strong>Gráficos:</strong> Balanço Geral (barras), Por Categoria (pizza), Histórico Mensal (linhas)</ListItem>
          <ListItem><strong>Filtros:</strong> clique em "Filtrar" para buscar por período, tipo, categoria ou descrição</ListItem>
          <ListItem><strong>Exportar CSV:</strong> clique no ícone de download para exportar os dados</ListItem>
        </UnorderedList>
      </>
    ),
    keywords: 'fluxo caixa receitas despesas gráficos csv exportar filtros transações',
  },
  {
    title: '3. Pagamentos',
    content: (
      <>
        <Text mb={3}>Lista todos os pagamentos processados via gateway Pagar.me.</Text>
        <Text fontWeight="semibold" mb={2}>Status dos pagamentos:</Text>
        <Table size="sm" mb={3} variant="simple">
          <Thead><Tr><Th>Status</Th><Th>Descrição</Th></Tr></Thead>
          <Tbody>
            <Tr><Td><Badge colorScheme="green" fontSize="xs">Pago</Badge></Td><Td>Pagamento confirmado e recebido</Td></Tr>
            <Tr><Td><Badge colorScheme="yellow" fontSize="xs">Pendente</Badge></Td><Td>Aguardando processamento ou confirmação</Td></Tr>
            <Tr><Td><Badge colorScheme="red" fontSize="xs">Falhou</Badge></Td><Td>Pagamento recusado pelo gateway</Td></Tr>
            <Tr><Td><Badge colorScheme="purple" fontSize="xs">Reembolsado</Badge></Td><Td>Valor devolvido ao profissional</Td></Tr>
            <Tr><Td><Badge colorScheme="orange" fontSize="xs">Contestado</Badge></Td><Td>Chargeback — disputa aberta pelo banco</Td></Tr>
          </Tbody>
        </Table>
        <Text>Filtre por status, data inicial e data final. O gráfico de barras mostra a receita por mês.</Text>
      </>
    ),
    keywords: 'pagamentos pagar.me status pago pendente falhou reembolsado contestado gateway cartão pix boleto',
  },
  {
    title: '4. Assinaturas',
    content: (
      <>
        <Text mb={3}>Mostra todas as assinaturas premium dos profissionais.</Text>
        <Text fontWeight="semibold" mb={2}>Planos disponíveis:</Text>
        <UnorderedList ml={6} spacing={1} mb={3}>
          <ListItem><strong>Mensal:</strong> R$ 29,90/mês</ListItem>
          <ListItem><strong>Trimestral:</strong> R$ 89,70 (3 meses)</ListItem>
          <ListItem><strong>Anual:</strong> R$ 358,80 (12 meses)</ListItem>
        </UnorderedList>
        <Text fontWeight="semibold" mb={2}>Status das assinaturas:</Text>
        <UnorderedList ml={6} spacing={1} mb={3}>
          <ListItem><Badge colorScheme="green" fontSize="xs">Ativa</Badge> — assinatura vigente com pagamento em dia</ListItem>
          <ListItem><Badge colorScheme="orange" fontSize="xs">Período de Carência</Badge> — pagamento vencido, ainda dentro do prazo de tolerância</ListItem>
          <ListItem><Badge colorScheme="red" fontSize="xs">Expirada</Badge> — assinatura vencida, sem renovação</ListItem>
          <ListItem><Badge colorScheme="gray" fontSize="xs">Cancelada</Badge> — profissional cancelou a renovação automática</ListItem>
        </UnorderedList>
        <Text>O gráfico de pizza mostra a distribuição por status. Os cards de resumo mostram contagens por estado.</Text>
      </>
    ),
    keywords: 'assinaturas premium planos mensal trimestral anual ativa carência expirada cancelada renovação',
  },
  {
    title: '5. Contas a Receber',
    content: (
      <>
        <Text mb={3}>Visão contábil dos recebimentos de assinaturas. Mesmos dados dos Pagamentos, mas apresentados como lançamentos contábeis.</Text>
        <UnorderedList ml={6} spacing={1} mb={3}>
          <ListItem><strong>Recebido</strong> — pagamentos confirmados (status "Pago")</ListItem>
          <ListItem><strong>A Receber</strong> — pagamentos pendentes de confirmação</ListItem>
          <ListItem><strong>Vencido</strong> — boletos vencidos sem pagamento</ListItem>
          <ListItem><strong>Estornado</strong> — valores devolvidos ou contestados</ListItem>
        </UnorderedList>
        <Text>O gráfico de barras mostra a evolução mensal dividida por situação.</Text>
      </>
    ),
    keywords: 'contas receber contábil recebido pendente vencido estornado lançamentos',
  },
  {
    title: '6. Contas a Pagar',
    content: (
      <>
        <Text mb={3}>Registro de despesas operacionais da plataforma.</Text>
        <UnorderedList ml={6} spacing={1} mb={3}>
          <ListItem>Clique em <strong>"Nova Despesa"</strong> para registrar uma despesa</ListItem>
          <ListItem>Preencha: descrição, categoria, valor, data de vencimento</ListItem>
          <ListItem>Use <strong>"Marcar Pago"</strong> na tabela para registrar o pagamento</ListItem>
          <ListItem>Filtre por categoria ou status (Pago, Pendente, Vencido)</ListItem>
        </UnorderedList>
        <Alert status="info" borderRadius="md" fontSize="sm">
          <AlertIcon />
          Atualmente as despesas são armazenadas na sessão. Uma futura atualização adicionará persistência no banco de dados.
        </Alert>
      </>
    ),
    keywords: 'contas pagar despesas operacionais categoria vencimento registrar',
  },
];

export default financeiroManual;
